import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './editDaycareDialog.module.scss'
import { Daycare } from '@/types/Daycare'
import { useMutation } from '@apollo/client'
import { ACTIVE_DAYCARE, DEACTIVE_DAYCARE } from '@/modules/API/daycare/daycareActivation'
import {
    SET_NEW_DAYCARE_ANNUAIRE_ID,
    SWITCH_DAYCARE_MEAL_ACCESS,
    SWITCH_DAYCARE_NEWS_ACCESS,
    SWITCH_DAYCARE_STAFF_ACCESS,
    SWITCH_DAYCARE_SUGGEST_ACCESS,
    SWITCH_DAYCARE_TASK_NOTE_ACCESS
} from '@/modules/API/software/daycareAccess'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import moment from 'moment/moment'
import UserInput from '@/components/_custom/UserInput/userInput'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker'
import { SET_VALIDITY_END } from '@/modules/API/daycare/daycareHandler'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { Check } from '@mui/icons-material'

interface EditDaycareDialogProps {
    isDialogOpen: boolean
    handleCloseDialog: () => void
    daycare: Daycare | null
    refreshDaycares: () => void
}

const EditDaycareDialog: FunctionComponent<EditDaycareDialogProps> = ({
    isDialogOpen,
    handleCloseDialog,
    daycare,
    refreshDaycares
}: EditDaycareDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [creationDate, setCreationDate] = useState<string>(
        moment(daycare?.created, 'DD-MM-YYYY HH:mm:ss').format('DD/MM/YYYY') || ''
    )
    const [daycareId, setDaycareId] = useState<string>(daycare?.id.toString() || '')
    const [annuaireId, setAnnuaireId] = useState<string>(daycare?.annuaireId || '')

    const [active, setActive] = useState<boolean>(daycare?.active || false)

    const [needToRefresh, setNeedToRefresh] = useState<boolean>(false)
    const [annuaireIdConfirmed, setAnnuaireIdConfirmed] = useState<boolean>(false)

    const [activeDaycare] = useMutation(ACTIVE_DAYCARE, {
        variables: {
            daycare_id: daycare?.id
        },
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('daycareActivated'))
        },
        onError: () => {
            showNotificationError(t('daycareActivatedError'))
        }
    })

    const [deactiveDaycare] = useMutation(DEACTIVE_DAYCARE, {
        variables: {
            daycare_id: daycare?.id
        },
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('daycareDeactivated'))
        },
        onError: () => {
            showNotificationError(t('daycareDeactivatedError'))
        }
    })

    function handleActiveChange(): void {
        setActive(!active)
        if (!active) {
            activeDaycare()
        } else {
            deactiveDaycare()
        }
    }

    const [validityDate, setValidityDate] = useState<string>(
        moment(daycare?.validityEnd, 'DD-MM-YYYY').format('DD-MM-YYYY') || ''
    )

    const [updateValidity] = useMutation(SET_VALIDITY_END, {
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('validityUpdated'))
        },
        onError: () => {
            showNotificationError(t('validityUpdatedError'))
        }
    })

    function handleUpdateValidity(value: string): void {
        updateValidity({
            variables: {
                daycareId: daycare?.id,
                date: value
            }
        })
        setValidityDate(value)
    }

    const [suggestAccess, setSuggestAccess] = useState<boolean>(daycare?.suggestAccess || false)

    const [switchSuggestAccess] = useMutation(SWITCH_DAYCARE_SUGGEST_ACCESS, {
        variables: {
            daycareId: daycare?.id
        },
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('suggestAccessSwitched'))
        },
        onError: () => {
            showNotificationError(t('suggestAccessSwitchedError'))
        }
    })

    function handleSuggestAccessChange(): void {
        setSuggestAccess(!suggestAccess)
        switchSuggestAccess()
    }

    const [staffAccess, setStaffAccess] = useState<boolean>(daycare?.staffAccess || false)

    const [switchStaffAccess] = useMutation(SWITCH_DAYCARE_STAFF_ACCESS, {
        variables: {
            daycareId: daycare?.id
        },
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('staffAccessSwitched'))
        },
        onError: () => {
            showNotificationError(t('staffAccessSwitchedError'))
        }
    })

    function handleStaffAccessChange(): void {
        setStaffAccess(!staffAccess)
        switchStaffAccess()
    }

    const [taskNoteAccess, setTaskNoteAccess] = useState<boolean>(daycare?.taskNoteAccess || false)

    const [switchTaskNoteAccess] = useMutation(SWITCH_DAYCARE_TASK_NOTE_ACCESS, {
        variables: {
            daycareId: daycare?.id
        },
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('taskNoteAccessSwitched'))
        },
        onError: () => {
            showNotificationError(t('taskNoteAccessSwitchedError'))
        }
    })

    function handleTaskNoteAccessChange(): void {
        setTaskNoteAccess(!taskNoteAccess)
        switchTaskNoteAccess()
    }

    const [newsAccess, setNewsAccess] = useState<boolean>(daycare?.newsAccess || false)

    const [switchNewsAccess] = useMutation(SWITCH_DAYCARE_NEWS_ACCESS, {
        variables: {
            daycareId: daycare?.id
        },
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('newsAccessSwitched'))
        },
        onError: () => {
            showNotificationError(t('newsAccessSwitchedError'))
        }
    })

    function handleNewsAccessChange(): void {
        setNewsAccess(!newsAccess)
        switchNewsAccess()
    }

    const [mealAccess, setMealAccess] = useState<boolean>(daycare?.mealAccess || false)

    const [switchMealAccess] = useMutation(SWITCH_DAYCARE_MEAL_ACCESS, {
        variables: {
            daycareId: daycare?.id
        },
        onCompleted: () => {
            setNeedToRefresh(true)

            showNotificationSuccess(t('mealAccessSwitched'))
        },
        onError: () => {
            showNotificationError(t('mealAccessSwitchedError'))
        }
    })

    function handleMealAccessChange(): void {
        setMealAccess(!mealAccess)
        switchMealAccess()
    }

    const [setNewAnnuaireId] = useMutation(SET_NEW_DAYCARE_ANNUAIRE_ID, {
        variables: {
            daycareId: daycare?.id,
            annuaireId: Number(annuaireId)
        },
        onCompleted: () => {
            setAnnuaireIdConfirmed(true)
            setNeedToRefresh(true)

            showNotificationSuccess(t('annuaireIdSet'))
        },
        onError: () => {
            showNotificationError(t('annuaireIdSetError'))
        }
    })

    function handleAnnuaireIdChange(): void {
        setAnnuaireId(annuaireId)
        setNewAnnuaireId()
    }

    useEffect(() => {
        setActive(daycare?.active || false)
        setCreationDate(moment(daycare?.created, 'DD-MM-YYYY HH:mm:ss').format('DD/MM/YYYY') || '')
        setDaycareId(daycare?.id.toString() || '')
        setValidityDate(moment(daycare?.validityEnd, 'DD-MM-YYYY').format('DD-MM-YYYY') || '')
        setSuggestAccess(daycare?.suggestAccess || false)
        setStaffAccess(daycare?.staffAccess || false)
        setNewsAccess(daycare?.newsAccess || false)
        setTaskNoteAccess(daycare?.taskNoteAccess || false)
        setMealAccess(daycare?.mealAccess || false)
        setAnnuaireId(daycare?.annuaireId?.toString() || '')
    }, [daycare])

    function closeDialog(): void {
        if (needToRefresh) refreshDaycares()
        handleCloseDialog()
    }

    return (
        <Dialog open={isDialogOpen} handleClose={closeDialog} className={styles.Dialog}>
            <DialogHeader title={daycare ? daycare.name : t('noDaycare')} />
            <DialogBody className={styles.DialogBody}>
                {daycare === null ? (
                    <p>{t('noDaycare')}</p>
                ) : (
                    <>
                        <div className={styles.InputContainer}>
                            <div className={styles.CheckboxContainer}>
                                <Checkbox text={t('activeDaycare')} checked={active} onChange={handleActiveChange} />
                            </div>
                        </div>
                        <div className={styles.DataContainer}>
                            <UserInput
                                value={daycareId}
                                setValue={setDaycareId}
                                readOnly
                                label={t('daycareId')}
                                className={styles.Input}
                            />
                            <UserInput
                                value={creationDate}
                                setValue={setCreationDate}
                                readOnly
                                label={t('creationDate')}
                                className={styles.Input}
                            />
                        </div>
                        <div className={styles.DataContainer}>
                            <DatePickerComponent
                                label={t('validityDate')}
                                value={validityDate}
                                className={styles.Input}
                                setValue={setValidityDate}
                                onChange={handleUpdateValidity}
                            />
                            <div className={styles.InputWithIcon}>
                                <UserInput
                                    value={annuaireId}
                                    setValue={(value) => {
                                        setAnnuaireId(value)
                                        setAnnuaireIdConfirmed(false)
                                    }}
                                    label={t('annuaireId')}
                                    className={styles.Input}
                                    type={'number'}
                                    verify={'number'}
                                />
                                {annuaireId !== (daycare.annuaireId?.toString() ?? '') && !annuaireIdConfirmed && (
                                    <div className={styles.Icon} onClick={handleAnnuaireIdChange}>
                                        <Check />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.InputContainer}>
                            <div className={styles.CheckboxContainer}>
                                <Checkbox
                                    text={t('daycareStaffAccess')}
                                    checked={staffAccess}
                                    onChange={handleStaffAccessChange}
                                />
                                <Checkbox
                                    text={t('newsAccess')}
                                    checked={newsAccess}
                                    onChange={handleNewsAccessChange}
                                />
                                <Checkbox
                                    text={t('suggestions')}
                                    checked={suggestAccess}
                                    onChange={handleSuggestAccessChange}
                                />
                                <Checkbox
                                    text={t('tasksAndNotes')}
                                    checked={taskNoteAccess}
                                    onChange={handleTaskNoteAccessChange}
                                />
                                <Checkbox text={t('meal')} checked={mealAccess} onChange={handleMealAccessChange} />
                            </div>
                        </div>
                    </>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={closeDialog} className={styles.Button} />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditDaycareDialog
